import { useTranslation } from "react-i18next";
import { SPECIES_KEYS, EPITAPH_FONTS, fontStyle } from "../lib/constants";
import { DateField } from "./DateField";

const FontPicker = ({ value, sample, onChange, prefix }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-3" role="radiogroup" aria-label={t("wizard.epitaph_font")} data-testid={`${prefix}-font-picker`}>
      <p className="text-xs font-semibold mb-2">{t("wizard.epitaph_font")}</p>
      <div className="flex flex-wrap gap-2">
        {Object.keys(EPITAPH_FONTS).map((k) => (
          <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)}
            className={`rounded-xl border px-3 py-2 text-start transition-colors ${value === k ? "border-[var(--gold)] bg-[hsl(var(--secondary))] ring-1 ring-[var(--gold)]" : "hover:border-[hsl(var(--accent))]"}`}
            data-testid={`${prefix}-font-${k}`}>
            <span className="block text-lg leading-tight truncate max-w-[9rem]" style={fontStyle(k)}>{(sample || "Per sempre").slice(0, 16)}</span>
            <span className="block text-[11px] text-[hsl(var(--muted-foreground))]">{t(`fonts.${k}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const Field = ({ id, label, hint, optional, children }) => {
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label} {optional && <span className="font-normal text-[hsl(var(--muted-foreground))]">({t("common.optional")})</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">{hint}</p>}
    </div>
  );
};

export const MemorialFields = ({ value, onChange, prefix = "pet" }) => {
  const { t } = useTranslation();
  const set = (k) => (e) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid gap-6">
      <div className="grid sm:grid-cols-2 gap-6">
        <Field id={`${prefix}-name`} label={`${t("wizard.name")} *`}>
          <input id={`${prefix}-name`} className="field-input" required maxLength={60} value={value.name} onChange={set("name")} placeholder={t("wizard.name_placeholder")} dir="auto" data-testid={`${prefix}-name-input`} />
        </Field>
        <Field id={`${prefix}-surname`} label={t("wizard.surname")} optional hint={t("wizard.surname_hint")}>
          <input id={`${prefix}-surname`} className="field-input" maxLength={60} value={value.surname} onChange={set("surname")} dir="auto" data-testid={`${prefix}-surname-input`} />
        </Field>
      </div>
      <fieldset>
        <legend className="field-label">{t("wizard.species")}</legend>
        <div className="flex flex-wrap gap-2" role="radiogroup">
          {SPECIES_KEYS.map((s) => (
            <button
              type="button"
              key={s}
              role="radio"
              aria-checked={value.species === s}
              onClick={() => onChange({ ...value, species: s })}
              data-testid={`${prefix}-species-${s}`}
              className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${value.species === s ? "bg-[var(--dusk)] text-[var(--cream)] border-[var(--dusk)]" : "hover:border-[hsl(var(--accent))]"}`}
            >
              {t(`species.${s}`)}
            </button>
          ))}
        </div>
      </fieldset>
      <div className="grid sm:grid-cols-3 gap-6">
        <Field id={`${prefix}-breed`} label={t("wizard.breed")} optional>
          <input id={`${prefix}-breed`} className="field-input" maxLength={60} value={value.breed} onChange={set("breed")} dir="auto" data-testid={`${prefix}-breed-input`} />
        </Field>
        <Field id={`${prefix}-birth`} label={t("wizard.birth_date")} optional>
          <DateField id={`${prefix}-birth`} value={value.birth_date || ""} onChange={(v) => onChange({ ...value, birth_date: v })} testId={`${prefix}-birth-input`} />
        </Field>
        <Field id={`${prefix}-death`} label={t("wizard.death_date")} optional>
          <DateField id={`${prefix}-death`} value={value.death_date || ""} onChange={(v) => onChange({ ...value, death_date: v })} testId={`${prefix}-death-input`} />
        </Field>
      </div>
      <Field id={`${prefix}-epitaph`} label={t("wizard.epitaph")} optional>
        <textarea id={`${prefix}-epitaph`} className="field-input min-h-[96px] text-lg" style={fontStyle(value.epitaph_font)} maxLength={140} value={value.epitaph} onChange={set("epitaph")} placeholder={t("wizard.epitaph_placeholder")} dir="auto" data-testid={`${prefix}-epitaph-input`} />
        <FontPicker value={value.epitaph_font || "classic"} sample={value.epitaph || value.name} onChange={(f) => onChange({ ...value, epitaph_font: f })} prefix={prefix} />
        <p className={`text-xs mt-1.5 text-end ${value.epitaph.length >= 130 ? "text-[var(--clay)]" : "text-[hsl(var(--muted-foreground))]"}`} data-testid={`${prefix}-epitaph-counter`}>
          {t("wizard.epitaph_counter", { count: value.epitaph.length })}
        </p>
      </Field>
    </div>
  );
};

export function validateFields(v) {
  if (!v.name.trim()) return "name_required";
  if (v.epitaph.length > 140) return "epitaph_too_long";
  if (v.birth_date && v.death_date && v.birth_date > v.death_date) return "dates_order";
  return null;
}
